import { useEffect, useMemo, useState } from 'react';
import { Merge, Loader2, AlertTriangle, Check, ChevronsUpDown, X } from 'lucide-react';
import { useApiUrl, useCustom, useList } from '@refinedev/core';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import type { User } from '../types';

interface MergeSummary {
  ordersCount: number;
  accessGrantsCount: number;
  paymentsCount: number;
  subscriptionsCount: number;
  channelPassesCount: number;
  userBonusesCount: number;
}

interface MergeUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Текущий пользователь — будет SURVIVOR (этот аккаунт остаётся). */
  survivorId: number;
  survivorName: string;
  /** Вызывается после успешного мерджа — для refetch. */
  onMerged: () => void;
}

/**
 * Диалог объединения двух аккаунтов одного человека.
 *
 * Текущий открытый юзер — SURVIVOR (останется), пользователь выбирает loser
 * через combobox с поиском по имени / username / email / telegramId / phone.
 * Перед мерджем подгружается summary (сколько заказов/доступов будет перенесено).
 */
export function MergeUserDialog({
  open,
  onOpenChange,
  survivorId,
  survivorName,
  onMerged,
}: MergeUserDialogProps): React.ReactElement {
  const apiUrl = useApiUrl();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedLoser, setSelectedLoser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Debounce 300ms — не дёргаем API на каждый символ.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reset при открытии диалога.
  useEffect(() => {
    if (open) {
      setSearch('');
      setDebouncedSearch('');
      setSelectedLoser(null);
      setError(null);
      setIsSubmitting(false);
      setPopoverOpen(false);
    }
  }, [open]);

  // Поиск юзеров через тот же endpoint, что и users list.
  const listResult = useList<User>({
    resource: 'users',
    pagination: { currentPage: 1, pageSize: 20, mode: 'server' },
    filters: useMemo(
      () =>
        debouncedSearch.length > 0
          ? [{ field: 'search', operator: 'contains' as const, value: debouncedSearch }]
          : [],
      [debouncedSearch],
    ),
    queryOptions: {
      enabled: open && debouncedSearch.length >= 2,
      retry: false,
    },
  });

  const candidates: User[] = useMemo(() => {
    const data = listResult.result?.data ?? [];
    // Не показываем survivor-а в списке — нельзя слить юзера в себя.
    return data.filter((u) => u.id !== survivorId);
  }, [listResult.result?.data, survivorId]);

  // Summary loser-а — что именно будет перенесено.
  const summaryResult = useCustom<{ data: MergeSummary }>({
    url: selectedLoser ? `${apiUrl}/users/${selectedLoser.id}/merge-summary` : '',
    method: 'get',
    queryOptions: { enabled: Boolean(selectedLoser), retry: false },
  });
  const summary = summaryResult.query.data?.data?.data ?? null;
  const summaryLoading = Boolean(selectedLoser) && summaryResult.query.isLoading;
  const summaryError = summaryResult.query.isError;

  const handleSubmit = async (): Promise<void> => {
    if (!selectedLoser || isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      const accessToken =
        typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;
      const response = await fetch(
        `${apiUrl}/users/${selectedLoser.id}/merge-into/${survivorId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
        },
      );
      const body = (await response.json()) as {
        success?: boolean;
        error?: { code?: string; message?: string };
        message?: string;
      };
      if (!response.ok || body.success === false) {
        throw new Error(body.error?.message ?? body.message ?? 'Не удалось объединить');
      }
      onMerged();
      onOpenChange(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Не удалось объединить');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !isSubmitting && onOpenChange(next)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Merge className="h-5 w-5" />
            Объединить с другим аккаунтом
          </DialogTitle>
          <DialogDescription>
            Текущий аккаунт <b>{survivorName}</b> (#{survivorId}) останется. Выберите
            аккаунт-дубликат — он будет деактивирован, его доступы / заказы / платежи /
            подписки перенесены сюда.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Аккаунт-дубликат</Label>
            <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={popoverOpen}
                  aria-controls="merge-loser-listbox"
                  className="w-full justify-between"
                  disabled={isSubmitting}
                >
                  {selectedLoser ? (
                    <span className="flex items-center gap-2 truncate">
                      <span className="truncate">{formatUserLine(selectedLoser)}</span>
                    </span>
                  ) : (
                    <span className="text-muted-foreground">
                      Поиск по имени, email, телеграму…
                    </span>
                  )}
                  {selectedLoser ? (
                    <X
                      className="ml-2 h-4 w-4 shrink-0 opacity-50 hover:opacity-100"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedLoser(null);
                      }}
                    />
                  ) : (
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                id="merge-loser-listbox"
                className="w-[var(--radix-popover-trigger-width)] p-0"
                align="start"
              >
                <Command shouldFilter={false}>
                  <CommandInput
                    placeholder="Введите минимум 2 символа…"
                    value={search}
                    onValueChange={setSearch}
                  />
                  <CommandList>
                    {debouncedSearch.length < 2 && (
                      <CommandEmpty>Введите минимум 2 символа для поиска.</CommandEmpty>
                    )}
                    {debouncedSearch.length >= 2 && listResult.query.isLoading && (
                      <div className="flex items-center justify-center gap-2 py-4 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Поиск…
                      </div>
                    )}
                    {debouncedSearch.length >= 2 &&
                      !listResult.query.isLoading &&
                      candidates.length === 0 && (
                        <CommandEmpty>Ничего не найдено.</CommandEmpty>
                      )}
                    {candidates.length > 0 && (
                      <CommandGroup>
                        {candidates.map((u) => (
                          <CommandItem
                            key={u.id}
                            value={String(u.id)}
                            onSelect={() => {
                              setSelectedLoser(u);
                              setPopoverOpen(false);
                            }}
                            className="flex flex-col items-start gap-0.5"
                          >
                            <div className="flex w-full items-center gap-2">
                              <Check
                                className={cn(
                                  'h-4 w-4 shrink-0',
                                  selectedLoser?.id === u.id ? 'opacity-100' : 'opacity-0',
                                )}
                              />
                              <span className="font-medium">{formatUserLine(u)}</span>
                            </div>
                            <div className="ml-6 text-xs text-muted-foreground">
                              #{u.id} · {formatUserSecondary(u)}
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {selectedLoser && (
            <div className="rounded-md border bg-muted/40 p-3 text-sm">
              <div className="mb-2 font-medium">
                Будет перенесено с #{selectedLoser.id} на #{survivorId}:
              </div>
              {summaryLoading && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Считаем…</span>
                </div>
              )}
              {summaryError && (
                <div className="flex items-center gap-2 text-destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <span>Не удалось получить summary</span>
                </div>
              )}
              {summary && (
                <ul className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  <li>
                    Заказы: <b>{summary.ordersCount}</b>
                  </li>
                  <li>
                    Доступы: <b>{summary.accessGrantsCount}</b>
                  </li>
                  <li>
                    Платежи: <b>{summary.paymentsCount}</b>
                  </li>
                  <li>
                    Подписки: <b>{summary.subscriptionsCount}</b>
                  </li>
                  <li>
                    Channel-passes: <b>{summary.channelPassesCount}</b>
                  </li>
                  <li>
                    Бонусы: <b>{summary.userBonusesCount}</b>
                  </li>
                </ul>
              )}
            </div>
          )}

          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-700 dark:text-amber-300">
            <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />
            Действие необратимо: loser-аккаунт получит deletionReason=MERGED, его email
            и telegramId обнулятся, RefreshToken будут удалены. Snapshot сохранится в
            UserMergeHistory для аудита.
          </div>

          {error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-sm text-destructive">
              {error}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Отмена
          </Button>
          <Button
            onClick={() => {
              void handleSubmit();
            }}
            disabled={!selectedLoser || isSubmitting || summaryLoading}
          >
            {isSubmitting ? 'Объединяем…' : 'Объединить'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function formatUserLine(u: User): string {
  const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
  if (name) {
    return name;
  }
  if (u.username) {
    return `@${u.username}`;
  }
  if (u.email) {
    return u.email;
  }
  if (u.telegramId) {
    return `tg:${u.telegramId}`;
  }
  return `#${u.id}`;
}

function formatUserSecondary(u: User): string {
  const parts: string[] = [];
  if (u.username) {
    parts.push(`@${u.username}`);
  }
  if (u.email) {
    parts.push(u.email);
  }
  if (u.telegramId) {
    parts.push(`tg:${u.telegramId}`);
  }
  if (u.phone) {
    parts.push(u.phone);
  }
  return parts.length > 0 ? parts.join(' · ') : 'нет контактов';
}
